import classNames from "classnames";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router";
import {
  CheckIcon,
  CopyIcon,
  SaveIcon,
  TrashIcon,
  ViewIcon,
} from "../../../../../../assets";
import {
  copyToClipboard,
  getLoggedUser,
  isViewedItem,
  openLinkInNewTab,
  saveViewedItem,
} from "../../../../../utils";
import {
  formatDate,
  getText,
  sanitizeHtml,
} from "../../../../../utils/_generic";
import {
  getHighlightedExcerpts,
  highlightFullText,
} from "../../../../../utils/_getHighlightedExcerpts";
import { saveDocumentWithNotify } from "../../../../../utils/_handleSaveDocument";
import { PDLButton, PDLTypography } from "../../../dna";
import { ActionButton } from "../../../global";

const JurisprudenceItem = ({
  item,
  index,
  searchTerm,
  pushDown = false,
  cardProfileStyle = false,
  onRemove,
}) => {
  const dispatch = useDispatch();

  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const [isSavingDocument, setIsSavingDocument] = useState(false);

  const loggedUser = useSelector(getLoggedUser);

  const isIpAccount = loggedUser.origem === "ip_auth";

  const itemId = item.id || `jurisprudencia-${index}`;

  const itemType = item.tipo;
  const isDocket = itemType && itemType === "Súmulas";

  const fullHtmlContent = sanitizeHtml(
    item.texto ||
      item.resumo ||
      item.summary ||
      `Ementa não disponível para o item ${itemId}`,
  );

  const textOnly = getText(fullHtmlContent);

  const opened = isViewedItem(itemId) && !cardProfileStyle;

  const excerpts = searchTerm
    ? getHighlightedExcerpts(textOnly, searchTerm, 270)?.join("")
    : null;

  const fullHighlightedText = searchTerm
    ? getHighlightedExcerpts(textOnly, searchTerm, textOnly.length + 1)?.join(
        " ",
      )
    : null;

  const excerptPlainText = getText(excerpts);

  const shouldShowToggle = excerptPlainText.length < textOnly.length;

  const displayContent = !shouldShowToggle
    ? fullHighlightedText || textOnly
    : expanded
      ? fullHighlightedText || textOnly
      : excerpts || textOnly.slice(0, 500);

  const highlightedTitle = highlightFullText(item.titulo || "", searchTerm);

  const displayTitle = highlightedTitle ? highlightedTitle : item.titulo || "";

  const cleanTitle = sanitizeHtml(displayTitle);

  const handleSaveDocument = () => {
    saveDocumentWithNotify({
      dispatch,
      setIsSavingDocument,
      documentoId: item?._id,
      id: item?.id,
      tipo: "Jurisprudencia",
      url: window.location.href,
    });
  };

  const renderActions = (className) => {
    return (
      <div className={classNames("btn-action-container flex gap-2", className)}>
        {item.link_integra && item.comintegra === "1" && (
          <ActionButton
            icon={<ViewIcon />}
            label="Ver acórdão"
            onClick={() => openLinkInNewTab(item.link_integra)}
          />
        )}
        {!isIpAccount && (
          <ActionButton
            icon={<SaveIcon />}
            onClick={handleSaveDocument}
            disabled={isSavingDocument}
            label={isSavingDocument ? "Salvando..." : "Salvar"}
          />
        )}

        <ActionButton
          icon={copied ? <CheckIcon /> : <CopyIcon />}
          label={copied ? "Copiado" : "Copiar"}
          onClick={() => {
            if (copied) return;
            setCopied(true);
            copyToClipboard(textOnly);
            setTimeout(() => setCopied(false), 2000);
          }}
        />
      </div>
    );
  };

  return (
    <div
      key={itemId}
      className={`pdl_components_lista_resultados_item mt-3 ${opened ? "opened" : ""} ${
        pushDown ? "push-down" : ""
      } ${cardProfileStyle ? "card-style" : ""}`}
    >
      {renderActions("mobile")}
      <Link
        to={`/jurisprudencia/${item.id}`}
        className="text-decoration-none"
        onClick={() => {
          saveViewedItem(itemId);
          sessionStorage.setItem("last-clicked-result-item", itemId);
        }}
      >
        <PDLTypography
          size="md"
          weight="semibold"
          variant="brand"
          upper
          className="title-link"
        >
          <span
            className="pdl-title-clamp-3 item-title"
            dangerouslySetInnerHTML={{
              __html: `${
                opened ? `<span class="opened-status">Acessado</span>` : ""
              } ${cleanTitle}`,
            }}
          />
        </PDLTypography>
      </Link>

      <div className="d-flex flex-row align-items-center justify-content-between my-2 flex-wrap-reverse">
        <div>
          <PDLTypography as="span" size="sm">
            Jurisprudência
          </PDLTypography>

          {item.categoria && (
            <>
              &nbsp;&bull;&nbsp;
              <PDLTypography as="span" size="sm">
                {item.categoria}
              </PDLTypography>
            </>
          )}

          {!!item.tribunal && (
            <>
              &nbsp;&bull;&nbsp;
              <PDLTypography as="span" size="sm">
                {item.tribunal}
              </PDLTypography>
            </>
          )}
          {!!item.data && (
            <>
              &nbsp;&bull;&nbsp;
              <PDLTypography as="span" size="sm">
                {formatDate(item?.data)}
              </PDLTypography>
            </>
          )}
          {!!item?.relator?.[0] && (
            <>
              {" "}
              &nbsp;&bull;&nbsp;
              <PDLTypography as="span" size="sm">
                {item.relator[0]}
              </PDLTypography>
            </>
          )}
        </div>

        {renderActions("desktop")}
      </div>

      <div className="text-container">
        <div
          className="content text-sm inline"
          dangerouslySetInnerHTML={{
            __html: `${!isDocket ? "<strong>EMENTA:</strong>" : ""} ${displayContent}`,
          }}
        />

        {shouldShowToggle && (
          <PDLButton
            className="ps-2 mt-1 inline align-baseline"
            variant="link"
            style={{
              cursor: "pointer",
              display: "inline",
              paddingLeft: 4,
            }}
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? "Ver menos" : "Ver mais"}
          </PDLButton>
        )}
      </div>

      {pushDown && (
        <div className="btn-action-container-footer">
          <div className="btn-action-group-left">
            {item.link_integra && item.comintegra === "1" && (
              <ActionButton
                icon={<ViewIcon />}
                label="Ver acórdão"
                onClick={() => openLinkInNewTab(item.link_integra)}
              />
            )}
            <ActionButton
              icon={copied ? <CheckIcon /> : <CopyIcon />}
              label={copied ? "Copiado" : "Copiar"}
              onClick={() => {
                if (copied) return;
                setCopied(true);
                copyToClipboard(textOnly);
                setTimeout(() => setCopied(false), 2000);
              }}
            />
          </div>
          {onRemove && (
            <div className="btn-action-group-right">
              <button
                type="button"
                className="btn-remove-saved"
                onClick={() => onRemove(item)}
              >
                <TrashIcon color="#8c0000" />
                <span>Remover de salvos</span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default JurisprudenceItem;
